import { OnInit, OnDestroy, Output, ViewChild, Inject, Directive, EventEmitter } from '@angular/core';
import { skip, takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { RegistryService } from '../service/registry.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { urldecode } from '../../shared-third-party/functions/urldecode';
import { urlencode } from '../../shared-third-party/functions/urlencode';
import { REGISTRY_STORAGE } from '../registry-storage.token';
import { cloneDeep } from '../../shared-third-party/functions/cloneDeep';
import { IRegistryState } from '../interfaces/iregistry-state';
import { StorageInterface } from '../interfaces/storage.interface';
import moment from 'moment';
import { isArray } from 'rxjs/internal-compatibility';

const ExtendedSearchTitle = 'Расширенный';
const SimpleSearchTitle = 'Свернуть';

@Directive()
export abstract class SearchForm implements OnInit, OnDestroy {
  @ViewChild('fullSearch', { static: true }) fullSearch: MatExpansionPanel | undefined;
  @ViewChild('simpleSearch', { static: true }) simpleSearch: MatExpansionPanel | undefined;
  form: FormGroup;
  needSearch = new FormControl(true);
  saveForm = new FormControl(false);
  @Output() searching = new EventEmitter<void>();
  @Output() search$: BehaviorSubject<any>;

  /**
   * Текст кнопки
   */
  get toggleButtonText(): string {
    if (this.isSimpleSearch) {
      return ExtendedSearchTitle;
    } else {
      return SimpleSearchTitle;
    }
  }

  /**
   * Параметры сортировки
   */
  get sortParams(): any {
    return this.registryService.sortParams;
  }

  /**
   * Текущие параметры
   */
  get currentState(): IRegistryState {
    const storageCurrentState = this.storage.currentState();
    const page = Number(storageCurrentState.page) || 1;
    const limit = Number(storageCurrentState.limit) || 12;

    return {
      limit,
      offset: (page - 1) * limit,
      sort: this.sortParams,
      searchForm: cloneDeep(this.form.value),
    };
  }

  /**
   * Количество на странице
   */
  set countItems(countItems: number) {
    this.registryService.countItems$.next(countItems);
  }

  public isSimpleSearch = true;
  protected datetimeControls: string[] = [];
  protected simpleSearchControls: string[] = ['simple'];
  protected fullSearchControls: string[] = [];
  protected unsubscribe$ = new Subject();

  /**
   * Конструктор
   */
  protected constructor(
    @Inject(REGISTRY_STORAGE) public storage: StorageInterface,
    protected registryService: RegistryService,
    form: FormGroup|null = null
  ) {
    this.form = form ?? new FormGroup({});
    this.search$ = new BehaviorSubject(null);
  }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // Предупреждение
    if (!this.form) {
      console.error('Необходимо определить form в дочернем компоненте');
    }
    // Открываем простой поиск
    this.simpleSearch?.open();

    // Инициализация
    this.storage.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((queryParams) => {
      for (const controlName in this.form.controls) {
        if (this.form.controls.hasOwnProperty(controlName)) {
          const value = queryParams[controlName];
          if (value) {
            // Для форм с переключателем расширенный/простой поиск
            if (this.isSimpleSearch && this.fullSearchControls.indexOf(controlName) >= 0) {
              this.toggle();
            }
          }
          this.form.get(controlName)?.patchValue(value, { emitEvent: false });
        }
      }
      this.searching.emit();
    });

    // При нажатии кнопки поиска необходимо обновить гет параметры
    this.search$.pipe(skip(1), takeUntil(this.unsubscribe$)).subscribe((conditionals) => {
      const queryParams = this.storage.currentState();
      for (const name in conditionals) {
        if (Object.prototype.hasOwnProperty.call(conditionals, name)) {
          if (conditionals[name] instanceof Date) {
            queryParams[name] = moment(conditionals[name]).format('DD.MM.YYYYTHH:mm:ss');
          } else if (isArray(conditionals[name])) {
            // Для обработки массивов доработать - на текущий момент в queryParams[name] запишется массив строк, а не строка.
            queryParams[name] = urlencode('[' + conditionals[name].map((item) => {
              return urldecode(item);
            }).join(',') + ']');
          } else if (conditionals[name] === null) {
            queryParams[name] = 'null';
          } else {
            if (conditionals[name]) {
              queryParams[name] = urlencode(urldecode(conditionals[name]));
            }
          }
        }
      }
      for (const query in queryParams) {
        if (Object.prototype.hasOwnProperty.call(queryParams, query)) {
          if (queryParams[query] === null || queryParams[query] === 'null') {
            delete queryParams[query];
            continue;
          }

          const contains = !!this.form.get(query);
          const disabled = contains && this.form.get(query)?.disabled;
          if (contains && disabled) {
            delete queryParams[query];
          }
        }
      }

      queryParams['page'] = '1';
      this.searching.emit();
      this.storage.changeState(queryParams);
    });
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy(): void {
    this.search$.complete();
    this.unsubscribe$.next(void null);
    this.unsubscribe$.complete();
  }

  /**
   * Свернуть/развернуть большой поиск
   */
  toggle(): void {
    if (this.isSimpleSearch) {
      for (const controlName of this.simpleSearchControls) {
        this.form.get(controlName)?.disable();
      }
      for (const controlName of this.fullSearchControls) {
        this.form.get(controlName)?.enable();
      }
      if (this.fullSearch) {
        this.fullSearch.open();
      }
      if (this.simpleSearch) {
        this.simpleSearch.close();
      }
    } else {
      for (const controlName of this.simpleSearchControls) {
        this.form.get(controlName)?.enable();
      }
      for (const controlName of this.fullSearchControls) {
        this.form.get(controlName)?.disable();
      }
      if (this.fullSearch) {
        this.fullSearch.close();
      }
      if (this.simpleSearch) {
        this.simpleSearch.open();
      }
    }
    this.isSimpleSearch = !this.isSimpleSearch;
    for (const controlName  in this.form.controls) {
      // if (this.form.controls.hasOwnProperty(controlName) && this.form.controls[controlName].enabled) {
      //   this.form.controls[controlName].setValue(this.form.controls[controlName].value, { emitEvent: false });
      // }
    }
  }

  /**
   * Поиск
   */
  search(): void {
    this.search$.next(this.form.value);
  }
}
